import { AxiosError } from 'axios';
import { isAxiosStatus, parseRetryAfterSeconds } from './errors';
export const AUTH_RATE_LIMIT_STORAGE_KEY = 'grand78_auth_rate_limit';
export const AUTH_RATE_LIMIT_EVENT = 'grand78:auth-rate-limit';
export const AUTH_RATE_LIMIT_ERROR_CODE = 'ERR_AUTH_RATE_LIMIT';
export const AUTH_RATE_LIMIT_FALLBACK_SECONDS = 60;
export const AUTH_RATE_LIMIT_PATHS = {
    login: '/v1/auth/login',
    register: '/v1/auth/register',
    verify: '/v1/auth/verify',
    passwordResetRequest: '/v1/auth/password-reset/request',
    passwordResetConfirm: '/v1/auth/password-reset/confirm',
};
const AUTH_RATE_LIMIT_PATH_LIST = [
    AUTH_RATE_LIMIT_PATHS.passwordResetRequest,
    AUTH_RATE_LIMIT_PATHS.passwordResetConfirm,
    AUTH_RATE_LIMIT_PATHS.login,
    AUTH_RATE_LIMIT_PATHS.register,
    AUTH_RATE_LIMIT_PATHS.verify,
];
function readMap() {
    try {
        const raw = localStorage.getItem(AUTH_RATE_LIMIT_STORAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object')
            return {};
        const now = Date.now();
        const out = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (typeof value === 'number' && Number.isFinite(value) && value > now) {
                out[key] = value;
            }
        }
        return out;
    }
    catch {
        return {};
    }
}
function writeMap(map) {
    const now = Date.now();
    const cleaned = {};
    for (const [key, until] of Object.entries(map)) {
        if (until > now)
            cleaned[key] = until;
    }
    if (Object.keys(cleaned).length === 0) {
        localStorage.removeItem(AUTH_RATE_LIMIT_STORAGE_KEY);
    }
    else {
        localStorage.setItem(AUTH_RATE_LIMIT_STORAGE_KEY, JSON.stringify(cleaned));
    }
    window.dispatchEvent(new CustomEvent(AUTH_RATE_LIMIT_EVENT));
}
export function matchAuthRateLimitPath(url) {
    if (!url)
        return null;
    const path = url
        .replace(/^https?:\/\/[^/?#]+/i, '')
        .split('?')[0]
        .split('#')[0]
        .replace(/\/+$/, '');
    for (const candidate of AUTH_RATE_LIMIT_PATH_LIST) {
        if (path === candidate || path.endsWith(candidate))
            return candidate;
    }
    return null;
}
export function matchAuthRateLimitPathFromConfig(config) {
    if (!config)
        return null;
    const url = String(config.url ?? '');
    const fromUrl = matchAuthRateLimitPath(url);
    if (fromUrl)
        return fromUrl;
    const base = String(config.baseURL ?? '').replace(/\/+$/, '');
    const relative = url.replace(/^\/+/, '');
    if (!base && !relative)
        return null;
    return matchAuthRateLimitPath(base ? `${base}/${relative}` : `/${relative}`);
}
export function getAuthCooldownRemaining(path) {
    const until = readMap()[path];
    if (typeof until !== 'number')
        return 0;
    const remainingMs = until - Date.now();
    if (remainingMs <= 0)
        return 0;
    return Math.ceil(remainingMs / 1000);
}
export function setAuthCooldown(path, retryAfterSeconds) {
    const seconds = Math.ceil(retryAfterSeconds);
    if (!Number.isFinite(seconds) || seconds <= 0)
        return;
    const map = readMap();
    const nextUntil = Date.now() + seconds * 1000;
    map[path] = Math.max(map[path] ?? 0, nextUntil);
    writeMap(map);
}
export function subscribeAuthRateLimit(cb) {
    const onCustom = () => cb();
    const onStorage = (event) => {
        if (event.key === AUTH_RATE_LIMIT_STORAGE_KEY || event.key === null)
            cb();
    };
    window.addEventListener(AUTH_RATE_LIMIT_EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
        window.removeEventListener(AUTH_RATE_LIMIT_EVENT, onCustom);
        window.removeEventListener('storage', onStorage);
    };
}
export function formatAuthRateLimitMessage(seconds) {
    return `Слишком много попыток. Повторите через ${seconds} сек.`;
}
export function applyAuthRateLimitFromError(err, path, fallback = AUTH_RATE_LIMIT_FALLBACK_SECONDS) {
    if (!isAxiosStatus(err, 429))
        return false;
    setAuthCooldown(path, parseRetryAfterSeconds(err, fallback));
    return true;
}
export function createAuthRateLimitError(retryAfterSeconds, config) {
    const seconds = Math.max(1, Math.ceil(retryAfterSeconds));
    const headers = { 'retry-after': String(seconds) };
    const data = {
        status: 429,
        error: 'Too Many Requests',
        message: 'Слишком много попыток, попробуйте позже',
        retryAfterSeconds: seconds,
    };
    return new AxiosError('Too Many Requests', AUTH_RATE_LIMIT_ERROR_CODE, config, undefined, {
        status: 429,
        statusText: 'Too Many Requests',
        headers,
        data,
        config: config ?? {},
    });
}
